<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import guessPaper from "~/features/computer/data/guess-paper-2026.json";

type LongChapterKey = keyof typeof guessPaper.long_questions;
type ShortChapterKey = keyof typeof guessPaper.short_questions;

const chapterLabels: Record<number, string> = {
  1: "Software Development",
  2: "Python",
  3: "Algorithms",
  4: "Data Structures",
  5: "Data Analytics",
  6: "Emerging Technologies",
  7: "Legal and Ethical Aspects",
  8: "Digital Literacy",
  9: "Entrepreneurship",
};

const chapterLinks: Record<number, string> = {
  1: "/11th/computer/sdlc/",
  2: "/11th/computer/python/",
  3: "/11th/computer/algorithm/",
  4: "/11th/computer/data-structures/",
  5: "/11th/computer/data-analytics/",
  6: "/11th/computer/emerging-tech/",
  7: "/11th/computer/legal-ethics/",
  8: "/11th/computer/digital-literacy/",
  9: "/11th/computer/entrepreneurship/",
};

const longQuestionGroups = (Object.entries(guessPaper.long_questions) as [
  LongChapterKey,
  (typeof guessPaper.long_questions)[LongChapterKey],
][])
  .map(([key, value]) => {
    const chapter = Number(key.replace("chapter_", ""));

    return {
      chapter,
      chapterName: chapterLabels[chapter],
      priority: value.priority,
      expected: value.expected,
      stars: value.priority === "critical" ? "***" : "**",
      badge:
        value.priority === "critical"
          ? "Critical pick"
          : value.priority === "high"
            ? "High pick"
            : "Medium pick",
      tone:
        value.priority === "critical"
          ? "border-amber-300 bg-gradient-to-br from-amber-100 via-orange-50 to-white dark:border-amber-800 dark:from-amber-950/40 dark:via-slate-950 dark:to-slate-900"
          : "border-sky-300 bg-gradient-to-br from-sky-100 via-cyan-50 to-white dark:border-sky-800 dark:from-sky-950/40 dark:via-slate-950 dark:to-slate-900",
      questions: value.questions,
    };
  })
  .sort((a, b) => {
    const order = [2, 1, 3];
    return order.indexOf(a.chapter) - order.indexOf(b.chapter);
  });

const shortQuestionGroups = (Object.entries(guessPaper.short_questions) as [
  ShortChapterKey,
  (typeof guessPaper.short_questions)[ShortChapterKey],
][])
  .map(([key, questions]) => {
    const chapter = Number(key.replace("chapter_", ""));

    return {
      chapter,
      chapterName: chapterLabels[chapter],
      questions: questions.filter((item) => item.weight >= 2),
    };
  })
  .sort((a, b) => {
    const order = [2, 1, 3];
    return order.indexOf(a.chapter) - order.indexOf(b.chapter);
  });

const hotQuestions = [
  "Python programs",
  "SDLC phases",
  "Algorithm + flowchart",
];

const importantQuestionHooks = [
  { title: "Python programs", to: "/11th/computer/python/solved-exercise/" },
  { title: "Algorithms", to: "/11th/computer/algorithm/" },
  { title: "SDLC", to: "/11th/computer/sdlc/" },
  { title: "Data structures", to: "/11th/computer/data-structures/" },
];

const relatedLinks = [
  { title: "Paper Pattern", to: "/11th/computer/paper-pattern/" },
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/" },
  { title: "Important Questions", to: "/11th/computer/important-questions/" },
  { title: "Chapter Notes", to: "/11th/computer/notes/" },
];

const faqs = [
  {
    question: "1st year computer guess paper 2026 kya hota hai?",
    answer:
      "Guess paper predicted questions ka set hota hai jo pairing scheme aur past paper trends ke basis par banaya jata hai.",
  },
  {
    question: "Kya guess paper exact exam paper hota hai?",
    answer:
      "Nahi, guess paper exact paper nahi hota lekin ismein included questions ke exam mein aane ke chances zyada hote hain.",
  },
  {
    question: "Guess paper se kitna paper cover ho sakta hai?",
    answer:
      "Agar guess paper sahi tarah se banaya gaya ho to 60-80% tak paper cover ho sakta hai, khas taur par long aur short questions.",
  },
  {
    question: "Guess paper kya hota hai?",
    answer:
      "Guess paper most expected questions ka shortlist hota hai jo pattern, pairing aur repeated exam focus ke basis par tayar kiya jata hai.",
  },
  {
    question: "Kya yeh exact paper hota hai?",
    answer:
      "Nahi. Yeh exact board paper nahi hota. Yeh predicted paper hota hai jisme most expected long, short aur practical questions highlight kiye jate hain.",
  },
  {
    question: "Kya sirf guess se pass ho sakte hain?",
    answer:
      "Pass ho sakta hai agar aap Ch 2, Ch 3, Ch 1 aur basic MCQs cover kar lein, lekin safe score ke liye pattern aur pairing scheme bhi sath dekhni chahiye.",
  },
];

function openPrintDialog() {
  if (import.meta.client) {
    window.print();
  }
}

usePageSeo({
  title: "11th Computer Guess Paper 2026 (Punjab Board) | Ustaad",
  description:
    "11th computer guess paper 2026 Punjab Board with most expected questions, predicted long questions, short questions, practical programs, MCQ focus and smart exam shortcut.",
  keywords:
    "11th computer guess paper 2026, class 11 computer guess paper, first year computer guess paper, punjab board computer guess paper",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Guess Paper", item: "/11th/computer/guess-paper/" },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-rose-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8 print:hidden">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-rose-700">Exam shortcut + predicted paper</p>
      <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">
        11th Computer Guess Paper 2026 (Punjab Board)
      </h1>

      <div class="mt-5 rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-300">Agar aap sirf yeh prepare karein:</p>
        <div class="mt-3 space-y-2 text-sm leading-7 text-slate-900 dark:text-slate-100">
          <p>- Chapter 2 (Python)</p>
          <p>- Chapter 3 (Algorithms)</p>
          <p>- Chapter 1 (SDLC)</p>
        </div>
        <p class="mt-4 text-base font-semibold text-emerald-700">70% paper cover ho jata hai.</p>
      </div>

      <div class="mt-6 flex flex-wrap gap-3">
        <a
          href="#guess-questions"
          class="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700"
        >
          View Questions
        </a>
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-rose-300 hover:text-rose-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-rose-600 dark:hover:text-rose-300"
          @click="openPrintDialog"
        >
          Download PDF
        </button>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-rose-300">Most Expected</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight">Super Hot Questions</h2>
        </div>
        <p class="text-sm font-medium text-slate-300">Do not miss these</p>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <article
          v-for="item in hotQuestions"
          :key="item"
          class="rounded-[22px] border border-white/10 bg-white/5 p-5"
        >
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-rose-300">Hot</p>
          <h3 class="mt-2 text-xl font-semibold text-white">{{ item }}</h3>
        </article>
      </div>
    </section>

    <section id="guess-questions" class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Long Questions</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Long Questions (Main Section)</h2>
        </div>
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400">Most expected by chapter</p>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
        Yeh page exact paper nahi batata, lekin pattern aur pairing ke base par strongest long questions ko pehle rank karta hai.
      </p>

      <div class="mt-5 grid gap-4 lg:grid-cols-3">
        <article
          v-for="item in longQuestionGroups"
          :key="item.chapter"
          :class="['rounded-[24px] border p-5', item.tone]"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{{ item.badge }}</p>
              <NuxtLink :to="chapterLinks[item.chapter]" class="mt-2 block text-xl font-semibold text-slate-950 hover:text-rose-700 dark:text-white dark:hover:text-rose-300">
                Chapter {{ item.chapter }} - {{ item.chapterName }}
              </NuxtLink>
            </div>
            <span class="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              {{ item.stars }}
            </span>
          </div>
          <p class="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">{{ item.expected }} expected long questions</p>
          <div class="mt-4 space-y-3">
            <div
              v-for="question in item.questions"
              :key="question.q"
              class="rounded-[18px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            >
              <p class="text-sm font-medium leading-7 text-slate-900 dark:text-slate-100">{{ question.q }}</p>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Short Questions</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">High + Medium Short Questions</h2>
        </div>
        <p class="text-sm font-medium text-slate-500 dark:text-slate-400">Selective shortlist</p>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
        Sirf woh short questions rakhe gaye hain jinke chances strong hain, taake page overloaded na lage.
      </p>

      <div class="mt-5 grid gap-4 lg:grid-cols-3">
        <article
          v-for="item in shortQuestionGroups"
          :key="item.chapter"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <NuxtLink :to="chapterLinks[item.chapter]" class="text-lg font-semibold text-slate-950 hover:text-rose-700 dark:text-white dark:hover:text-rose-300">
            Chapter {{ item.chapter }} - {{ item.chapterName }}
          </NuxtLink>
          <div class="mt-4 space-y-3">
            <div
              v-for="question in item.questions"
              :key="question.q"
              class="rounded-[18px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            >
              <p class="text-sm font-medium leading-7 text-slate-900 dark:text-slate-100">{{ question.q }}</p>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
      <div class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Practical</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Must Practice Programs</h2>
        </div>
        <div class="mt-5 space-y-3">
          <div
            v-for="item in guessPaper.practical"
            :key="item"
            class="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          >
            {{ item }}
          </div>
        </div>
      </div>

      <div class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">MCQs Focus</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">MCQ Topics Only</h2>
        </div>
        <div class="mt-5 space-y-3">
          <div
            v-for="topic in guessPaper.mcq_topics"
            :key="topic"
            class="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          >
            {{ topic }}
          </div>
        </div>
      </div>
    </section>

    <section class="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <div class="rounded-[28px] border border-slate-200 bg-slate-950 p-5 text-white md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-rose-300">Smart Strategy</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight">What to prepare for marks</h2>
        <div class="mt-5 space-y-4 text-sm leading-7 text-slate-200">
          <div>
            <p class="font-semibold text-white">70% ke liye:</p>
            <p>- {{ guessPaper.strategy["70_percent"].join(", ") }}</p>
          </div>
          <div>
            <p class="font-semibold text-white">Pass ke liye:</p>
            <p>- {{ guessPaper.strategy.pass.join(", ") }}</p>
          </div>
          <div>
            <p class="font-semibold text-white">Skip:</p>
            <p>- {{ guessPaper.strategy.skip.join(", ") }}</p>
          </div>
        </div>
      </div>

      <div class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Important Questions</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Important Questions Hook</h2>
        <div class="mt-5 space-y-3">
          <NuxtLink
            v-for="item in importantQuestionHooks"
            :key="item.to"
            :to="item.to"
            class="block rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 transition hover:border-rose-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-rose-700 dark:hover:bg-slate-900"
          >
            {{ item.title }}
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Links</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Use this page after pattern and pairing</h2>
      </div>
      <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
        Yeh guess paper page tab strongest hota hai jab user paper pattern, pairing scheme aur important questions dekh chuka ho.
      </p>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <NuxtLink
          v-for="item in relatedLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-rose-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-rose-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-stone-200 bg-stone-50 px-5 py-6 dark:border-slate-800 dark:bg-slate-950 md:px-8 print:hidden">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500 dark:text-slate-400">PDF Block</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Printable guess paper, same data</h2>
        </div>
        <div class="flex flex-wrap gap-3">
          <a
            href="#printable-guess-paper"
            class="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
          >
            View PDF
          </a>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:border-rose-300 hover:text-rose-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-rose-600 dark:hover:text-rose-300"
            @click="openPrintDialog"
          >
            Download PDF
          </button>
        </div>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6 print:hidden">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Guess Paper FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>

    <section class="print:pt-0">
      <div
        id="printable-guess-paper"
        class="mx-auto max-w-[760px] rounded-[28px] border border-stone-200 bg-white px-6 py-8 shadow-[0_18px_60px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 md:px-12 md:py-12 print:max-w-none print:rounded-none print:border-0 print:px-0 print:py-0 print:shadow-none"
      >
        <div class="border-b border-dashed border-stone-300 pb-6 dark:border-slate-700">
          <p class="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500 dark:text-slate-400">Printable Guess Paper</p>
          <h2 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
            {{ guessPaper.meta.title }} {{ guessPaper.meta.year }} ({{ guessPaper.meta.board }})
          </h2>
          <p class="mt-3 text-base leading-8 text-slate-700 dark:text-slate-300">
            Shortcut route: Chapter 2, Chapter 3 aur Chapter 1 par focus karke most expected long questions, short questions aur practical prep cover karein.
          </p>
        </div>

        <div class="mt-8 grid gap-4 md:grid-cols-2">
          <section class="rounded-[22px] border border-stone-200 bg-stone-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">Top expected long questions</h3>
            <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
              <p
                v-for="item in longQuestionGroups.flatMap((group) => group.questions.slice(0, 2).map((question) => `${group.chapter}: ${question.q}`))"
                :key="item"
              >
                {{ item }}
              </p>
            </div>
          </section>

          <section class="rounded-[22px] border border-stone-200 bg-stone-50 p-5 dark:border-slate-800 dark:bg-slate-950">
            <h3 class="text-xl font-semibold text-slate-950 dark:text-white">Smart strategy</h3>
            <div class="mt-4 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
              <p>70%: {{ guessPaper.strategy["70_percent"].join(", ") }}</p>
              <p>Pass: {{ guessPaper.strategy.pass.join(", ") }}</p>
              <p>Skip: {{ guessPaper.strategy.skip.join(", ") }}</p>
            </div>
          </section>
        </div>

        <div class="mt-10">
          <h3 class="text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Practical focus</h3>
          <div class="mt-4 space-y-3 text-base leading-8 text-slate-700 dark:text-slate-300">
            <p v-for="item in guessPaper.practical.slice(0, 3)" :key="item">{{ item }}</p>
          </div>
        </div>
      </div>
    </section>
  </PageContainer>
</template>
